import React, { useState, useContext } from "react";
import {
  Box,
  Button,
  Divider,
  Grid,
  OutlinedInput,
  Typography,
} from "@mui/material";
import { PersonOutline } from "@mui/icons-material";
import { useIsMobile } from "../../../util/utils";
import { UserContext } from "../../../components/auth/auth-context";
import FeatureImage1 from "../../../assets/images/feature-image-1.png";
import FeatureImage2 from "../../../assets/images/feature-image-2.png";
import FeatureImage3 from "../../../assets/images/feature-image-3.png";
import FeatureImage4 from "../../../assets/images/feature-image-4.png";

const BusinessLandingPage: React.FC = () => {
  const userContext = useContext(UserContext);
  const [email, setEmail] = useState("");
  const [error, setError] = useState(false);
  const isMobile = useIsMobile();

  const featureCards = [
    {
      title: "Tailored Scheduling",
      description:
        "Adjust your scheduling preferences to fit your lifestyle. Plany365 empowers you to tailor the tool to meet your specific requirements.",
      image: FeatureImage1,
      alt: "Tailored Scheduling",
    },
    {
      title: "Smart Staff & Business Coordination",
      description:
        "Effortlessly manage your team across multiple locations. Assign schedules, track availability, and coordinate efficiently within your business to enhance productivity.",
      image: FeatureImage2,
      alt: "Smart Staff & Business Coordination",
    },
    {
      title: "Multi-Channel Notifications",
      description:
        "Stay informed with real-time updates via email and in-app alerts. Plany365 ensures you never miss a booking change, reminder, or important update.",
      image: FeatureImage3,
      alt: "Multi-Channel Notifications",
    },
    {
      title: "Multi-Branch Management",
      description:
        "Manage multiple business locations seamlessly under one account. Keep track of appointments, staff availability, and customer bookings across different branches with ease.",
      image: FeatureImage4, // Ensure you add an appropriate image here
      alt: "Multi-Branch Management",
    },
  ];

  const handleEmailSubmit = () => {
    if (/^\S+@\S+\.\S+$/.test(email)) {
      window.location.href = `/signup?email=${email}`;
    } else {
      setError(true);
    }
  };

  return (
    <Box sx={{ background: "white" }}>
      {/* Jumbotron Section */}
      <Box
        sx={{
          minHeight: isMobile ? "auto" : "500px",
          marginTop: userContext.providerUser ? "40px" : "30px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            maxWidth: "1200px",
            margin: "0 auto",
          }}
        >
          {/* Left Section */}
          <Box
            sx={{
              width: isMobile ? "100%" : "70%",
              padding: "60px",
              display: "flex",
              flexDirection: "column",
              gap: "20px",
              justifyContent: "space-between",
              height: "100%",
            }}
          >
            {/* Welcome Section */}
            <Box
              sx={{ textAlign: userContext.providerUser ? "left" : "center" }}
            >
              <Typography
                variant="h3"
                sx={{
                  margin: 0,
                  fontSize: `${
                    45 -
                    (userContext.providerUser?.username?.split(" ")[0]
                      ?.length || 0) *
                      1.3
                  }px`,
                }}
              >
                {userContext.providerUser?.username
                  ? `Welcome Back, ${
                      userContext.providerUser?.username.split(" ")[0]
                    }`
                  : "Plany365"}
              </Typography>
              <Typography variant="h6">
                {userContext.providerUser ? (
                  <>
                    Discover the powerful features of Plany365 that simplify
                    your scheduling and availability.
                    <br />
                    <br />
                    Whether you're a business, a professional, or managing
                    personal bookings —{" "}
                    <strong>create a business profile</strong> using your brand
                    name or personal name to organize services, events, classes,
                    and related appointments.
                  </>
                ) : (
                  <>
                    Plany365 is a smart scheduling platform built for managing
                    services, events, and classes — all through a flexible{" "}
                    <strong>business-based system</strong>.
                  </>
                )}
              </Typography>
            </Box>

            {/* Email Input for New Users */}
            {!userContext.providerUser.userId && (
              <Box
                sx={{
                  display: "flex",
                  gap: "10px",
                  marginTop: "20px",
                  alignItems: "center",
                }}
              >
                <OutlinedInput
                  value={email}
                  type="email"
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError(false);
                  }}
                  sx={{
                    color: error ? "red" : "black",
                    paddingLeft: "2%",
                    width: "100%",
                  }}
                  placeholder="Enter your email"
                  error={error}
                />
                <Button
                  onClick={handleEmailSubmit}
                  variant="contained"
                  sx={{
                    background: "background.paper",
                    maxHeight: "51px",
                    fontSize: "15px",
                    width: isMobile ? "100%" : "auto",
                  }}
                >
                  Get Started
                </Button>
              </Box>
            )}
            {/* My Account Button */}
            {userContext.providerUser.defaultBusinessId && (
              <Box sx={{ textAlign: "center", marginTop: "20px" }}>
                <Button
                  onClick={() => {
                    window.location.href = "/biz/calendar";
                  }}
                  startIcon={<PersonOutline />}
                  variant="contained"
                  sx={{ borderRadius: "25px" }}
                >
                  My Account
                </Button>
              </Box>
            )}

            {userContext.providerUser.userId &&
              !userContext.providerUser.defaultBusinessId && (
                <Box sx={{ textAlign: "center", marginTop: "20px" }}>
                  <Button
                    onClick={() => {
                      window.location.href = "/biz/calendar";
                    }}
                    startIcon={<PersonOutline />}
                    variant="contained"
                    sx={{ borderRadius: "25px" }}
                  >
                    Create your first business profile
                  </Button>
                </Box>
              )}
          </Box>
        </Box>
      </Box>

      <Divider />

      {/* Features Section */}
      <Box sx={{ padding: "10px 20px" }}>
        <Typography
          variant="h5"
          sx={{ fontWeight: "bold", marginBottom: "20px" }}
        >
          Features of Plany365
        </Typography>

        <Grid container spacing={4}>
          {featureCards.map((card, index) => (
            <Grid
              key={index}
              item
              xs={12}
              md={6}
              sx={{
                display: "flex",
                flexDirection: isMobile
                  ? "column"
                  : index % 2 === 0
                  ? "row-reverse"
                  : "row",
                alignItems: "center",
                padding: "10px", // Adds space inside the Grid items
              }}
            >
              {/* Image Section */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: isMobile ? "center" : "flex-start",
                  paddingTop: "15px",
                  paddingBottom: "15px",
                  paddingLeft: index % 2 === 0 ? { md: "40px", xs: "0" } : "0", // Adds left padding when image is on the left
                  paddingRight: index % 2 === 1 ? { md: "40px", xs: "0" } : "0", // Adds right padding when image is on the right
                }}
              >
                <img
                  src={card.image}
                  alt={card.alt}
                  style={{
                    width: "100%",
                    height: "250px",
                    objectFit: "cover",
                    borderRadius: "8px",
                  }}
                />
              </Grid>
              {/* Content Section */}
              <Grid
                item
                xs={12}
                md={6}
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "center",
                  alignItems: isMobile ? "center" : "flex-start",
                  paddingTop: "15px",
                  paddingBottom: "15px",
                }}
              >
                <Typography
                  variant="h6"
                  sx={{ fontWeight: "bold", marginBottom: "10px" }}
                >
                  {card.title}
                </Typography>
                <Typography sx={{ textAlign: isMobile ? "center" : "left" }}>
                  {card.description}
                </Typography>
              </Grid>
            </Grid>
          ))}
        </Grid>
      </Box>
      {/* <Box
        sx={{
          width: "100%",
          mt: 4,
          minHeight: "500px", // fallback height
          height: "auto",
          overflow: "hidden",
        }}
      >
        <iframe
          src="https://plany365.com/micrologic/booking/call-us"
          style={{
            width: "100%",
            minHeight: "800px", // better for mobile
            border: "none",
          }}
          title="Book a Call with MicroLogic"
          scrolling="yes"
        />
      </Box> */}
    </Box>
  );
};

export default BusinessLandingPage;
